import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useState } from "react";
import { PencilIcon } from "@/components/developer/DashboardIcons";
import CompanyPreferencesModal from "./CompanyPreferencesModal";
import { Skeleton } from "@/components/ui/skeleton";

const CompanyPreferences = () => {
  const { toast } = useToast();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { data: profile, isFetching, refetch } = useQuery({
    queryKey: ['developer-profile-company'],
    queryFn: async () => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('No user found');

      const { data, error } = await supabase
        .from('developer_profiles')
        .select('*')
        .eq('user_id', user.user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        toast({
          title: "Error",
          description: "Failed to fetch profile data",
          variant: "destructive",
        });
        throw error;
      }

      return data;
    }
  });

  if (isFetching) {
    return (
      <Card className="p-6">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-semibold">Company preferences</h2>
          <Skeleton className="h-9 w-9" />
        </div>
        <div className="space-y-6">
          <div className="space-y-4">
            <div>
              <Skeleton className="h-4 w-16 mb-2" />
              <Skeleton className="h-5 w-48" />
            </div>
            <div>
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-5 w-32" />
            </div>
            <div>
              <Skeleton className="h-4 w-28 mb-2" />
              <Skeleton className="h-5 w-40" />
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between mb-4">
        <h2 className="text-xl font-semibold">Company preferences</h2>
        <Button variant="ghost" size="icon" onClick={() => setIsEditModalOpen(true)}>
          <PencilIcon className="h-4 w-4" />
        </Button>
      </div>
      <div className="space-y-4">
        <div>
          <p className="text-sm font-medium mb-1">Company size</p>
          <p className="text-sm text-gray-600">
            {profile?.company_size?.length ? profile.company_size.join(', ') : 'No preference'}
          </p>
        </div>
        <div>
          <p className="text-sm font-medium mb-1">Investment stage</p>
          <p className="text-sm text-gray-600">
            {profile?.investment_stage?.length ? profile.investment_stage.join(', ') : 'No preference'}
          </p>
        </div>
        <div>
          <p className="text-sm font-medium mb-1">Industries</p>
          <p className="text-sm text-gray-600">
            {profile?.industry?.length ? profile.industry.join(', ') : 'Not specified'}
          </p>
        </div>
      </div>

      <CompanyPreferencesModal 
        refetchSection={refetch}
        isOpen={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        currentPreferences={{
          industry: profile?.industry || [],
          company_size: profile?.company_size || [],
          investment_stage: profile?.investment_stage || []
        }}
      />
    </Card>
  );
};

export default CompanyPreferences;